import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import type { CSSProperties } from "react";
import { api } from "../utils/api";
import { slugifySeriesName } from "../utils/slug";
import { CAR_CATALOG } from "../utils/carCatalog";
import { CarBadge } from "./CarBadge";

interface Props {
  teamId: string;
  onClose: () => void;
  onCreated: () => void;
}

// Create an EMPTY custom series container (#282): name + declared cars. Tracks are
// added into it afterward on its SeriesHome via the track picker.
export default function CreateSeriesModal({ teamId, onClose, onCreated }: Props) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [carIds, setCarIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const prevActive = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialogRef.current?.querySelector<HTMLElement>("input, button")?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !submitting) { e.preventDefault(); onClose(); return; }
      if (e.key !== "Tab") return;
      const list = Array.from(dialogRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])') || []);
      if (!list.length) return;
      const first = list[0], last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      prevActive?.focus?.();
    };
  }, [onClose, submitting]);

  const toggle = (id: string) =>
    setCarIds((p) => (p.includes(id) ? p.filter((c) => c !== id) : [...p, id]));

  const canSubmit = !!name.trim() && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const s = await api<{ id: string; name: string; slug: string }>("/api/custom-series", {
        method: "POST",
        body: JSON.stringify({ teamId, name: name.trim(), carIds }),
      });
      onCreated();
      navigate(`/teams/${teamId}/series/${s.slug || slugifySeriesName(s.name)}`);
    } catch (err: any) {
      setError(err?.message || "Could not create the series.");
      setSubmitting(false);
    }
  };

  return createPortal(
    <div style={styles.overlay} onMouseDown={(e) => e.target === e.currentTarget && !submitting && onClose()}>
      <div ref={dialogRef} className="card" style={styles.modal} role="dialog" aria-modal="true" aria-label="New custom series" tabIndex={-1}>
        <div style={styles.head}>
          <span className="eyebrow">New custom series</span>
          <h2 style={styles.title}>Start a custom series</h2>
          <p style={styles.dek}>Name your league or event and pick its cars. You'll add tracks next.</p>
        </div>

        <label style={styles.field}>
          <span className="section-label" style={{ margin: 0 }}>Series name</span>
          <input className="input" placeholder="Friday Night League" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
        </label>

        <div role="group" aria-label="Cars" style={styles.field}>
          <span className="section-label" style={{ margin: 0 }}>Cars</span>
          <div style={styles.carGrid}>
            {CAR_CATALOG.map((car) => {
              const on = carIds.includes(car.id);
              return (
                <button key={car.id} type="button" className={`pill ${on ? "pill--live" : ""}`} style={styles.carChip} aria-pressed={on} onClick={() => toggle(car.id)}>
                  <CarBadge slug={car.id} size={20} />
                  {car.label}
                </button>
              );
            })}
          </div>
          <div style={styles.hint}>
            Cup, Xfinity and Trucks have full fuel + pit support; the rest are declarable.
            {carIds.length === 0 && " Declare at least one car to scope Fuel and Pit per car."}
          </div>
        </div>

        {error && <div style={styles.error}>{error}</div>}

        <div style={styles.actions}>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={submit} disabled={!canSubmit}>
            {submitting ? "Creating…" : "Create series"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

const styles: Record<string, CSSProperties> = {
  overlay: { position: "fixed", inset: 0, zIndex: 1000, background: "rgba(6,8,12,0.72)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "4.5rem 1rem", overflowY: "auto" },
  modal: { width: "100%", maxWidth: "520px", padding: "1.6rem 1.6rem 1.3rem", display: "flex", flexDirection: "column", gap: "1rem" },
  head: { display: "flex", flexDirection: "column", gap: "0.3rem" },
  title: { fontFamily: "var(--font-display)", fontSize: "1.5rem", fontWeight: 700, margin: 0, color: "var(--text-strong)" },
  dek: { margin: 0, fontSize: "0.9rem", color: "var(--text-muted)", lineHeight: 1.45 },
  field: { display: "flex", flexDirection: "column", gap: "0.4rem" },
  carGrid: { display: "flex", flexWrap: "wrap", gap: "0.45rem" },
  carChip: { cursor: "pointer", border: "1px solid var(--rule)", display: "inline-flex", alignItems: "center", gap: "0.4rem" },
  hint: { fontSize: "0.8125rem", color: "var(--text-muted)", lineHeight: 1.4 },
  error: { fontSize: "0.875rem", color: "var(--flag-red)", lineHeight: 1.4 },
  actions: { display: "flex", justifyContent: "flex-end", gap: "0.6rem", marginTop: "0.4rem" },
};
